import { useEffect, useState, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Link } from 'react-router-dom';
import { Search, Layers, MapPin } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CENTER = [46.3844, 16.4342]; // Čakovec
const LAYERS_KEY = 'emina_map_layers';

const LAYER_DEFS = [
  { key: 'articles', label: 'Članci', color: '#e11d48' },
  { key: 'classifieds', label: 'Oglasi', color: '#f59e0b' },
  { key: 'jobs', label: 'Posao', color: '#16a34a' },
  { key: 'events', label: 'Događanja', color: '#3b82f6' },
];

function coloredIcon(color) {
  const html = `<div style="background:${color};width:18px;height:18px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,0.4)"></div>`;
  return L.divIcon({ className: '', html, iconSize: [22, 22], iconAnchor: [11, 22] });
}

function FlyTo({ pos }) {
  const map = useMap();
  useEffect(() => { if (pos) map.flyTo(pos, 13, { duration: 0.6 }); }, [pos, map]);
  return null;
}

export default function MapPage({ compact = false }) {
  const [markers, setMarkers] = useState([]);
  const [active, setActive] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LAYERS_KEY)) || ['articles', 'classifieds', 'jobs', 'events']; }
    catch { return ['articles', 'classifieds', 'jobs', 'events']; }
  });
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [flyTo, setFlyTo] = useState(null);
  const [showSidebar, setShowSidebar] = useState(!compact);

  useEffect(() => { localStorage.setItem(LAYERS_KEY, JSON.stringify(active)); }, [active]);

  useEffect(() => {
    fetch(`${API}/public/map/markers?types=${active.join(',')}`).then(r => r.json()).then(d => setMarkers(d.markers || []));
  }, [active]);

  function toggleLayer(k) {
    setActive(a => a.includes(k) ? a.filter(x => x !== k) : [...a, k]);
  }

  async function doSearch(e) {
    e.preventDefault();
    if (search.trim().length < 2) return;
    const r = await fetch(`${API}/public/geocode?q=${encodeURIComponent(search)}`);
    const d = await r.json();
    setResults(d.results || []);
    if (d.results?.[0]) setFlyTo([d.results[0].lat, d.results[0].lon]);
  }

  if (compact) {
    return (
      <div className="bg-white dark:bg-[#0f172a] rounded-xl border border-gray-100 dark:border-gray-800 overflow-hidden" data-testid="home-minimap">
        <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
          <h3 className="text-sm font-bold uppercase tracking-wider text-gray-900 dark:text-gray-100 border-l-4 border-red-600 pl-3 flex items-center gap-1.5"><MapPin size={14} /> Mapa Međimurja</h3>
          <Link to="/mapa" className="text-[10px] text-red-600 dark:text-red-400 font-medium">Otvori →</Link>
        </div>
        <div className="h-48">
          <MapContainer center={CENTER} zoom={10} scrollWheelZoom={false} style={{ width: '100%', height: '100%' }}>
            <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            {markers.slice(0, 30).map(m => (
              <Marker key={`${m.type}-${m.id}`} position={[m.lat, m.lng]} icon={coloredIcon(m.color)}>
                <Popup><Link to={m.url} className="text-xs font-semibold text-red-600">{m.title}</Link></Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet><title>Mapa Međimurja — E-MINA</title></Helmet>
      <div className="flex flex-col lg:flex-row h-[calc(100vh-120px)]" data-testid="map-page">
        {/* Sidebar */}
        <aside className={`lg:w-80 xl:w-96 overflow-y-auto bg-white dark:bg-[#0f172a] border-r border-gray-200 dark:border-gray-800 ${showSidebar ? '' : 'hidden'}`}>
          <div className="p-4 space-y-4">
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-1">Interaktivna mapa</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">Članci, oglasi, posao i događanja u Međimurju.</p>
            </div>

            {/* Search */}
            <form onSubmit={doSearch} className="flex gap-2">
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Pretraži lokaciju…"
                className="flex-1 bg-gray-50 dark:bg-[#0b1220] border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm outline-none focus:border-red-400"
                data-testid="map-search-input" />
              <button className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg" data-testid="map-search-btn"><Search size={14} /></button>
            </form>
            {results.map((r, i) => (
              <button key={i} onClick={() => setFlyTo([r.lat, r.lon])}
                className="w-full text-left text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 px-2 py-1 rounded">
                {r.display_name}
              </button>
            ))}

            {/* Layers */}
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-1"><Layers size={12} /> Slojevi</p>
              <div className="space-y-1.5">
                {LAYER_DEFS.map(l => (
                  <label key={l.key} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`map-layer-${l.key}`}>
                    <input type="checkbox" checked={active.includes(l.key)} onChange={() => toggleLayer(l.key)} />
                    <span className="w-3 h-3 rounded-full" style={{ background: l.color }} />
                    <span className="text-gray-700 dark:text-gray-200">{l.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Visible markers list */}
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">Oznake ({markers.length})</p>
              <ul className="space-y-2 max-h-64 overflow-y-auto">
                {markers.map(m => (
                  <li key={`${m.type}-${m.id}`} className="border border-gray-100 dark:border-gray-800 rounded-lg p-2 cursor-pointer hover:border-red-400" onClick={() => setFlyTo([m.lat, m.lng])}>
                    <div className="flex items-center gap-1.5 text-[10px] uppercase font-semibold mb-0.5" style={{ color: m.color }}>
                      <span className="w-2 h-2 rounded-full" style={{ background: m.color }} /> {m.type}
                    </div>
                    <p className="text-xs font-medium text-gray-900 dark:text-gray-100 line-clamp-2">{m.title}</p>
                    {m.location && <p className="text-[10px] text-gray-500 dark:text-gray-400">{m.location}</p>}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </aside>

        {/* Map */}
        <div className="flex-1 relative">
          <MapContainer center={CENTER} zoom={10} scrollWheelZoom style={{ width: '100%', height: '100%' }}>
            <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <FlyTo pos={flyTo} />
            {markers.map(m => (
              <Marker key={`${m.type}-${m.id}`} position={[m.lat, m.lng]} icon={coloredIcon(m.color)}>
                <Popup>
                  <div className="w-48">
                    {m.cover_image && <img src={m.cover_image} alt="" className="w-full h-24 object-cover rounded mb-2" />}
                    <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: m.color }}>{m.type}</p>
                    <p className="text-sm font-semibold mb-1">{m.title}</p>
                    {m.snippet && <p className="text-xs text-gray-600 line-clamp-2 mb-2">{m.snippet}</p>}
                    <Link to={m.url} className="text-xs font-semibold text-red-600 hover:underline">Otvori →</Link>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      </div>
    </>
  );
}
